import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  getDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION_NAME = 'bookings';

// Get bookings by company
export const getBookingsByCompany = async (companyId, filters = {}) => {
  try {
    let constraints = [where('companyId', '==', companyId)];
    
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    const q = query(collection(db, COLLECTION_NAME), ...constraints);
    const querySnapshot = await getDocs(q);
    
    let bookings = [];
    querySnapshot.forEach((doc) => {
      bookings.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by createdAt desc
    bookings.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
      return dateB - dateA;
    });
    
    return { bookings, error: null };
  } catch (error) {
    console.error('Error getting bookings by company:', error);
    return { bookings: [], error: error.message };
  }
};

// Get all bookings (admin)
export const getAllBookings = async (filters = {}) => {
  try {
    let constraints = [];
    
    if (filters.companyId) {
      constraints.push(where('companyId', '==', filters.companyId));
    }
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    const q = constraints.length > 0 
      ? query(collection(db, COLLECTION_NAME), ...constraints)
      : query(collection(db, COLLECTION_NAME));
    
    const querySnapshot = await getDocs(q);
    
    let bookings = [];
    querySnapshot.forEach((doc) => {
      bookings.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by createdAt desc
    bookings.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
      return dateB - dateA;
    });
    
    return { bookings, error: null };
  } catch (error) {
    console.error('Error getting all bookings:', error);
    return { bookings: [], error: error.message };
  }
};

// Get bookings by user
export const getBookingsByUser = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    
    let bookings = [];
    querySnapshot.forEach((doc) => {
      bookings.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by createdAt desc
    bookings.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
      return dateB - dateA;
    });
    
    return { bookings, error: null };
  } catch (error) {
    console.error('Error getting bookings by user:', error);
    return { bookings: [], error: error.message };
  }
};

// Get bookings by car (for availability checking)
export const getBookingsByCar = async (carId, dateRange = {}) => {
  try {
    let constraints = [
      where('carId', '==', carId),
      where('status', '==', 'confirmed') // Only confirmed bookings block calendar
    ];
    
    const q = query(collection(db, COLLECTION_NAME), ...constraints);
    const querySnapshot = await getDocs(q);
    
    let bookings = [];
    querySnapshot.forEach((doc) => {
      bookings.push({ id: doc.id, ...doc.data() });
    });
    
    // Filter by date range if provided
    if (dateRange.from && dateRange.to) {
      bookings = bookings.filter(booking => {
        const bookingFrom = new Date(booking.fromDateTime);
        const bookingTo = new Date(booking.toDateTime);
        const rangeFrom = new Date(dateRange.from);
        const rangeTo = new Date(dateRange.to);
        
        // Check for overlap
        return bookingFrom < rangeTo && bookingTo > rangeFrom;
      });
    }
    
    return { bookings, error: null };
  } catch (error) {
    console.error('Error getting bookings by car:', error);
    return { bookings: [], error: error.message };
  }
};

// Get single booking by ID
export const getBookingById = async (bookingId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, bookingId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { 
        booking: { id: docSnap.id, ...docSnap.data() }, 
        error: null 
      };
    }
    return { booking: null, error: 'Booking not found' };
  } catch (error) {
    console.error('Error getting booking:', error);
    return { booking: null, error: error.message };
  }
};

// Create booking
export const createBooking = async (data) => {
  try {
    // Generate booking ID like BK-20260205-XXXX
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    const bookingNumber = `BK-${dateStr}-${random}`;
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...data,
      bookingNumber,
      status: data.status || 'confirmed',
      paymentStatus: data.paymentStatus || 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { id: docRef.id, bookingNumber, error: null };
  } catch (error) {
    console.error('Error creating booking:', error);
    return { id: null, bookingNumber: null, error: error.message };
  }
};

// Update booking
export const updateBooking = async (bookingId, data) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, bookingId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    
    return { error: null };
  } catch (error) {
    console.error('Error updating booking:', error);
    return { error: error.message };
  }
};

// Add booking image
export const addBookingImage = async (bookingId, imageData) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, bookingId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return { error: 'Booking not found' };
    }
    
    const booking = docSnap.data();
    const imageType = imageData.type; // 'before-ride' | 'after-ride'
    const imagesField = imageType === 'before-ride' ? 'beforeRide' : 'afterRide';
    
    // Get existing images array
    const existingImages = booking.images?.[imagesField] || [];
    
    // Check limit (15 images max per type)
    if (existingImages.length >= 15) {
      return { error: 'Maximum 15 images allowed per section' };
    }
    
    // Add new image to array
    const updatedImages = [...existingImages, imageData];
    
    // Update booking with new images array
    await updateDoc(docRef, {
      [`images.${imagesField}`]: updatedImages,
      updatedAt: serverTimestamp()
    });
    
    return { error: null };
  } catch (error) {
    console.error('Error adding booking image:', error);
    return { error: error.message };
  }
};

// Delete booking image
export const deleteBookingImage = async (bookingId, imageId, imageType, imagePath) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, bookingId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return { error: 'Booking not found' };
    }
    
    const booking = docSnap.data();
    const imagesField = imageType === 'before-ride' ? 'beforeRide' : 'afterRide';
    const existingImages = booking.images?.[imagesField] || [];
    
    // Find the image to get its path if not provided
    const imageToDelete = existingImages.find(img => img.id === imageId);
    const pathToDelete = imagePath || imageToDelete?.path;
    
    // Remove image from array
    const updatedImages = existingImages.filter(img => img.id !== imageId);
    
    // Update booking
    await updateDoc(docRef, {
      [`images.${imagesField}`]: updatedImages,
      updatedAt: serverTimestamp()
    });
    
    // Delete from storage if path provided
    if (pathToDelete) {
      const { deleteImage } = await import('./storage');
      await deleteImage(pathToDelete);
    }
    
    return { error: null };
  } catch (error) {
    console.error('Error deleting booking image:', error);
    return { error: error.message };
  }
};

// Cancel booking
export const cancelBooking = async (bookingId, reason, cancelledBy) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, bookingId);
    await updateDoc(docRef, {
      status: 'cancelled',
      cancelledAt: serverTimestamp(),
      cancelledBy,
      cancellationReason: reason || 'No reason provided',
      updatedAt: serverTimestamp()
    });
    
    return { error: null };
  } catch (error) {
    console.error('Error cancelling booking:', error);
    return { error: error.message };
  }
};

// Check car availability
export const checkCarAvailability = async (carId, fromDateTime, toDateTime) => {
  try {
    const result = await getBookingsByCar(carId, {
      from: fromDateTime,
      to: toDateTime
    });
    
    return { 
      available: result.bookings.length === 0, 
      conflictingBookings: result.bookings,
      error: null 
    };
  } catch (error) {
    console.error('Error checking car availability:', error);
    return { available: false, conflictingBookings: [], error: error.message };
  }
};

// Get booking stats by company
export const getBookingStats = async (companyId) => {
  try {
    const result = await getBookingsByCompany(companyId);
    
    const now = new Date();
    const stats = {
      total: result.bookings.length,
      confirmed: result.bookings.filter(b => b.status === 'confirmed').length,
      completed: result.bookings.filter(b => b.status === 'completed').length,
      cancelled: result.bookings.filter(b => b.status === 'cancelled').length,
      pending: result.bookings.filter(b => b.status === 'pending').length,
      
      // Financial stats
      totalRevenue: result.bookings
        .filter(b => b.status === 'confirmed' || b.status === 'completed')
        .reduce((sum, b) => sum + (b.totalAmount || 0), 0),
      
      // Upcoming bookings (future confirmed)
      upcoming: result.bookings.filter(b => 
        b.status === 'confirmed' && new Date(b.fromDateTime) > now
      ).length,
    };
    
    return { stats, error: null };
  } catch (error) {
    console.error('Error getting booking stats:', error);
    return { stats: null, error: error.message };
  }
};

// Get all booking stats (admin)
export const getAllBookingStats = async () => {
  try {
    const result = await getAllBookings();
    
    const now = new Date();
    const stats = {
      total: result.bookings.length,
      confirmed: result.bookings.filter(b => b.status === 'confirmed').length,
      completed: result.bookings.filter(b => b.status === 'completed').length,
      cancelled: result.bookings.filter(b => b.status === 'cancelled').length,
      pending: result.bookings.filter(b => b.status === 'pending').length,
      
      totalRevenue: result.bookings
        .filter(b => b.status === 'confirmed' || b.status === 'completed')
        .reduce((sum, b) => sum + (b.totalAmount || 0), 0),
      
      upcoming: result.bookings.filter(b => 
        b.status === 'confirmed' && new Date(b.fromDateTime) > now
      ).length,
    };
    
    // Group by company
    const byCompany = {};
    result.bookings.forEach(booking => {
      if (!byCompany[booking.companyId]) {
        byCompany[booking.companyId] = { count: 0, revenue: 0 };
      }
      byCompany[booking.companyId].count++;
      if (booking.status === 'confirmed' || booking.status === 'completed') {
        byCompany[booking.companyId].revenue += booking.totalAmount || 0;
      }
    });
    
    stats.byCompany = byCompany;
    
    return { stats, error: null };
  } catch (error) {
    console.error('Error getting all booking stats:', error);
    return { stats: null, error: error.message };
  }
};
